import React, { useMemo } from 'react';
import { X, ChevronRight, AlertTriangle, Clock } from 'lucide-react';
import { Employee } from '../../types/backend.types';
import { TimeEntryRow, formatHM } from './timecard.types';
import { isoToEasternDate } from '../../utils/easternTime';

interface Props {
  current: Employee;
  employees: Employee[];
  entries: TimeEntryRow[];        // all entries for the pay period (every employee)
  jumpableIds: Set<number>;       // employee_ids present in the roster (can navigate to)
  weekLabel: string;
  onJump: (employeeId: number) => void;
  onClose: () => void;
}

const pad = (n: number) => String(n).padStart(2, '0');
// Eastern calendar date so "days worked" counts buckets on the Eastern day,
// regardless of the admin's own timezone.
const localDateStr = (iso: string) => isoToEasternDate(iso);
const isForeman = (e: Employee) => e.role === 'foreman' || e.role === 'asphalt_foreman';

export const CrewHoursModal: React.FC<Props> = ({
  current, employees, entries, jumpableIds, weekLabel, onJump, onClose,
}) => {
  const { foreman, members, rows, maxHours, crewTotal, median } = useMemo(() => {
    // Whose crew? If viewing a foreman, it's their own crew; otherwise their foreman's.
    const foremanId = isForeman(current) ? current.employee_id : current.crew_foreman_id;
    const foreman = foremanId ? employees.find((e) => e.employee_id === foremanId) : undefined;

    // crew = the foreman + everyone whose crew_foreman_id points at them
    const memberList = foremanId
      ? employees.filter((e) => e.employee_id === foremanId || e.crew_foreman_id === foremanId)
      : [current];

    const statFor = (empId: number) => {
      const es = entries.filter((e) => e.employee_id === empId);
      const hours = es.reduce((s, e) => s + (Number(e.hours_worked) || 0), 0);
      const days = new Set(es.map((e) => localDateStr(e.clock_in))).size;
      const flagged = es.filter((e) => e.needs_review).length;
      const open = es.filter((e) => !e.clock_out).length;
      return { hours, days, flagged, open };
    };

    const rows = memberList
      .map((e) => ({ emp: e, ...statFor(e.employee_id) }))
      .sort((a, b) => b.hours - a.hours);

    // Median over people who actually logged time (robust to 0h members).
    const worked = rows.filter((r) => r.hours > 0).map((r) => r.hours).sort((a, b) => a - b);
    const median = worked.length
      ? worked.length % 2
        ? worked[(worked.length - 1) / 2]
        : (worked[worked.length / 2 - 1] + worked[worked.length / 2]) / 2
      : 0;

    const maxHours = Math.max(1, ...rows.map((r) => r.hours));
    const crewTotal = rows.reduce((s, r) => s + r.hours, 0);
    return { foreman, members: memberList, rows, maxHours, crewTotal, median };
  }, [current, employees, entries]);

  const OUTLIER_THRESHOLD = 2; // hours off the crew median

  const hasCrew = members.length > 1;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', width: '100%', maxWidth: 520, borderRadius: '16px 16px 0 0', maxHeight: '88vh', overflowY: 'auto', padding: '18px 16px 28px', boxShadow: '0 -8px 30px rgba(0,0,0,0.2)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937' }}>Crew hours</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              {foreman ? `${foreman.first_name} ${foreman.last_name}'s crew` : 'Crew'} · {weekLabel}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}>
            <X size={22} color="#64748b" />
          </button>
        </div>

        {!hasCrew ? (
          <div style={{ textAlign: 'center', color: '#64748b', padding: '32px 16px' }}>
            <Clock size={32} color="#94a3b8" style={{ marginBottom: 10 }} />
            <div style={{ fontWeight: 600, color: '#1f2937' }}>No crew assigned</div>
            <div style={{ fontSize: 14 }}>This employee isn't linked to a foreman's crew.</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
              {rows.map(({ emp, hours, days, flagged, open }) => {
                const isCurrent = emp.employee_id === current.employee_id;
                const jumpable = jumpableIds.has(emp.employee_id) && !isCurrent;
                const zero = hours === 0;
                const delta = !zero && median ? hours - median : 0;
                const outlier = Math.abs(delta) >= OUTLIER_THRESHOLD;
                return (
                  <button
                    key={emp.employee_id}
                    onClick={() => (jumpable ? onJump(emp.employee_id) : undefined)}
                    style={{
                      textAlign: 'left', width: '100%', border: isCurrent ? '1px solid #c7d2fe' : '1px solid #eef2f7',
                      background: isCurrent ? '#eef2ff' : '#fff', borderRadius: 10, padding: '10px 12px',
                      cursor: jumpable ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', gap: 6,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: zero ? '#94a3b8' : '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {emp.first_name} {emp.last_name}
                        </span>
                        {isForeman(emp) && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#3730a3', background: '#e0e7ff', borderRadius: 10, padding: '2px 7px', textTransform: 'uppercase' }}>
                            Foreman
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{ fontSize: 16, fontWeight: 800, color: zero ? '#dc2626' : '#1f2937' }}>
                          {formatHM(hours)}
                        </span>
                        {jumpable && <ChevronRight size={16} color="#cbd5e1" />}
                      </div>
                    </div>

                    {/* comparison bar */}
                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(hours / maxHours) * 100}%`, background: zero ? '#fca5a5' : isCurrent ? '#6366f1' : '#94a3b8', borderRadius: 3 }} />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: '#64748b', flexWrap: 'wrap' }}>
                      <span>{zero ? 'No time logged' : `${days} day${days === 1 ? '' : 's'}`}</span>
                      {outlier && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#b45309', background: '#fef3c7', borderRadius: 10, padding: '1px 8px', fontWeight: 700 }}>
                          <AlertTriangle size={11} /> {Math.abs(delta).toFixed(1)}h {delta < 0 ? 'under' : 'over'} crew
                        </span>
                      )}
                      {flagged > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#d97706', fontWeight: 600 }}>
                          <AlertTriangle size={12} /> {flagged} flagged
                        </span>
                      )}
                      {open > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#ea580c', fontWeight: 600 }}>
                          <Clock size={12} /> {open} open
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, padding: '12px 12px 0', borderTop: '1px solid #f1f5f9', fontSize: 14 }}>
              <span style={{ color: '#64748b' }}>Crew total · {rows.length} {rows.length === 1 ? 'person' : 'people'}</span>
              <span style={{ fontWeight: 800, color: '#1f2937' }}>{formatHM(crewTotal)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
